<template>
  <div>
    <v-container v-if="form" class="wrapper">
      <v-layout>
        <v-text-field
          v-if="canEdit"
          v-model="form.name"
          label="案件名（必須）"
          placeholder="案件1"
        />
        <FormGroup v-if="!canEdit">
          <FormLabel>案件名</FormLabel>
          <div>{{ form.name }}</div>
        </FormGroup>
      </v-layout>
      <v-layout v-if="!insertionOrder.isNew" column mb-4>
        <FormLabel>ステータス</FormLabel>
        <v-switch
          v-if="canEdit"
          v-model="form.status"
          :label="statusLabel"
          true-value="active"
          false-value="inactive"
        />
        <div v-if="!canEdit">{{ statusLabel }}</div>
      </v-layout>
      <FormGroup v-show="!isAdvertiser" class="flex xs6 mb-4">
        <AgencyMarginSelect
          v-if="canEdit && insertionOrder.isNew"
          v-model="form.agencyMarginId"
          :agency-margin-list="agencyMarginList"
          :loading="loading.agencyMarginList"
        />
        <template v-if="!(canEdit && insertionOrder.isNew)">
          <FormLabel>代理店マージン</FormLabel>
          <div>{{ agencyMarginName }}</div>
        </template>
      </FormGroup>
      <FormGroup class="flex xs6">
        <SalesAccountSelect
          v-if="canEdit"
          v-model="form.salesAccountId"
          :sales-account-list="salesAccountList"
          :loading="loading.salesAccountList"
        />
        <template v-if="!canEdit">
          <FormLabel>担当営業</FormLabel>
          <div>{{ salesAccountName }}</div>
        </template>
      </FormGroup>
      <FormGroup>
        <FormLabel>案件予算</FormLabel>
        <v-radio-group
          v-if="insertionOrder.isNew"
          v-model="willEditBudgetList"
          class="form-group"
        >
          <v-radio :value="false" label="今は設定しない" />
          <v-radio :value="true" label="設定する" />
        </v-radio-group>
        <v-slide-y-transition>
          <div v-if="willEditBudgetList" class="form-group">
            <v-layout>
              <AddBudgetButton
                v-if="canEdit"
                :insertion-order="insertionOrder"
                :advertiser="advertiser"
                :budget-list="budgetList"
                :is-upper-budget-limit="isUpperBudgetLimit"
                :addDeadlineDate="addDeadlineDate"
                :deliveryLimit="deliveryLimit"
                @addBudget="onAddBudget"
              />
              <v-spacer />
              <v-checkbox
                v-if="!insertionOrder.isNew"
                v-model="showNewRequest"
                label="未承認の予算も表示する"
                class="pt-0 mt-0 align-end justify-end"
                hide-details
              />
            </v-layout>
            <BudgetTable
              :show-new-request="showNewRequest || insertionOrder.isNew"
              :insertion-order="insertionOrder"
              :budget-list="budgetList"
              :advertiser="advertiser"
              :deliveryLimit="deliveryLimit"
              :loading="budgetListLoading"
              :can-edit="canEdit"
              :is-upper-budget-limit="isUpperBudgetLimit"
              @cancelRequest="onCancelRequest"
            />
          </div>
        </v-slide-y-transition>
      </FormGroup>
      <AdvancedSetting button-text="詳細設定">
        <EditDetailForm
          :advertiser="advertiser"
          :form="form"
          :can-edit="canEdit"
        />
      </AdvancedSetting>
    </v-container>
  </div>
</template>

<script>
import AdvancedSetting from '../../../../Shared/CrudUI/AdvancedSetting.vue'
import {
  actions as snackbarActions,
  snackbarType,
} from '@/vuex/modules/ui/snackbar/constants'
import FormGroup from './FormGroup'
import FormLabel from './FormLabel'
import AgencyMarginSelect from './AgencyMarginSelect'
import SalesAccountSelect from './SalesAccountSelect'
import AddBudgetButton from '../Button/AddBudgetButton'
import BudgetTable from '../Budget/BudgetTable'
import EditDetailForm from './EditDetailForm'
import {
  CreateInsertionOrdersForm,
  InsertionOrdersModel,
  UpdateInsertionOrdersForm,
} from '@/models/InsertionOrders'
import { mapGetters } from 'vuex'
import {
  actions,
  getters,
} from '@/vuex/modules/advertiser/insertionOrders/constants'

export default {
  name: 'EditForm',
  components: {
    FormGroup,
    FormLabel,
    AgencyMarginSelect,
    SalesAccountSelect,
    AddBudgetButton,
    BudgetTable,
    EditDetailForm,
    AdvancedSetting,
  },
  props: {
    insertionOrder: {
      type: Object,
      default: () => {},
    },
    advertiser: {
      type: Object,
      default: () => {},
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    permissionLevel: {
      type: String,
      default: '',
    },
    salesAccountList: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Object,
      default: () => {},
    },
    addDeadlineDate: {
      type: Date,
      required: true,
    },
    deliveryLimit: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      form: null,
      budgetListLoading: false,
      willEditBudgetList: true,
      showNewRequest: true,
      loadingAccountList: true,
    }
  },
  computed: {
    ...mapGetters('account', ['isAdvertiser']),
    statusLabel() {
      return this.form.status ? '有効' : '無効'
    },
    isValid() {
      return (
        this.form && this.form.isValid && !this.insertionOrder.isOverBudgetLimit
      )
    },
    isUpperBudgetLimit() {
      if (this.insertionOrder.isNew) {
        return this.budgetList.length >= InsertionOrdersModel.BUDGET_LIMIT
      } else {
        return (
          this.insertionOrder.filteredValidBudgetListLength >=
          InsertionOrdersModel.BUDGET_LIMIT
        )
      }
    },
    agencyMarginName() {
      if (!this.insertionOrder.agencyMarginId) return ''
      const agencyMargin = this.agencyMarginList.find(
        m => m.value === this.insertionOrder.agencyMarginId,
      )
      return agencyMargin && agencyMargin.text
    },
    salesAccountName() {
      if (!this.insertionOrder.salesAccountId) return ''
      const salesAccount = this.salesAccountList.find(
        a => a.id === this.insertionOrder.salesAccountId,
      )
      return salesAccount && salesAccount.name
    },
    budgetList() {
      if (this.insertionOrder.isNew) {
        return this.form.budgetRequestList
      } else {
        return this.insertionOrder.budgetList
      }
    },
    agencyMarginList() {
      return this.$store.getters[getters.agencyMarginList]
    },
  },
  watch: {
    insertionOrder(newIO) {
      if (newIO) {
        this.setUpForm()
      }
    },
    isValid(validity) {
      this.$emit('validate', validity)
    },
    async 'insertionOrder.budgetList'() {
      if (!this.insertionOrder.isNew && this.insertionOrder.isOverBudgetLimit) {
        const statusError = await this.$confirm('案件予算上限エラー', {
          okText: '閉じる',
          onlyOk: true,
          body: `設定されている案件予算数が上限の52を超えているため、当案件は編集できません。
          詳しくはお問い合わせください。`,
        })
        if (statusError) return
      }
    },
  },
  async created() {
    // agencyMargin取得
    await this.getAgencyMarginList(Number(this.$route.params.agencyId))
    if (this.insertionOrder) this.setUpForm()
  },

  methods: {
    async getAgencyMarginList(agencyId) {
      await this.$store.dispatch(actions.GET_AGENCY_MARGIN_LIST, agencyId)
    },
    async setUpForm() {
      if (this.insertionOrder.isNew) {
        // defaultの代理店マージンを追加
        const defaultAgencyMargin = this.agencyMarginList.find(
          agm => agm.isDefault === true,
        )
        const insertionOrder = Object.assign(this.insertionOrder, {
          agencyMarginId: defaultAgencyMargin.value,
        })
        this.form = new CreateInsertionOrdersForm(insertionOrder)
      } else {
        this.form = new UpdateInsertionOrdersForm(this.insertionOrder)
        this.budgetListLoading = true
        await this.insertionOrder.fetchBudgetList()
        this.budgetListLoading = false
      }
    },
    clear() {
      this.form = null
    },
    async submit() {
      this.$store.dispatch('advertiser/insertionOrders/SET_LOADING', 'send')
      try {
        const insertionOrder = await this.form.save()
        this.$emit('success', insertionOrder)
      } catch (e) {
        const message = '保存に失敗しました'
        this.$store.dispatch(snackbarActions.SHOW_SNACKBAR, {
          type: snackbarType.ERROR,
          message,
        })
      }
      this.$store.dispatch('advertiser/insertionOrders/CLEAR_LOADING', 'send')
    },
    onAddBudget(budgetList) {
      this.form.budgetRequestList = [
        ...this.form.budgetRequestList,
        ...budgetList,
      ]
    },
    onCancelRequest(budget) {
      this.form.budgetRequestList = this.form.budgetRequestList.filter(
        item => item.tempId != budget.tempId,
      )
    },
  },
}
</script>

<style scoped lang="scss">
.wrapper {
  font-size: 16px;
}
.expandButton {
  width: 100%;
  margin: 0;
  padding: 0;

  ::v-deep .v-btn__content {
    justify-content: flex-start;
  }
}
</style>
